import { TFunction } from 'react-i18next';
import * as Yup from 'yup';
import { Shape } from '../yup_types';
import { validateUrl } from '../../utils/validators';

export type SiteTypes = {
	[key in FORM_FIELDS]: string | string[] | any;
};

export interface ISiteFormProps {
	Name: string;
	DescriptorCity: string;
	Descriptor: string;
	CustomerSupportName: string;
	CustomerSupportLink: string;
	CustomerSupportPhone: string;
	CustomerSupportEmail: string;
	WhitelistedUrls: string[];
	Tags: string[];
}

export enum FORM_FIELDS {
	NAME = 'Name',
	CITY = 'DescriptorCity',
	DESCRIPTOR = 'Descriptor',
	CONTACT_NAME = 'CustomerSupportName',
	URL = 'CustomerSupportLink',
	PHONE = 'CustomerSupportPhone',
	EMAIL = 'CustomerSupportEmail',
	WHITELISTED_URLS = 'WhitelistedUrls',
	TAGS = 'Tags'
}

export enum FORM_FIELDS_NAMES {
	NAME = 'SITES.LABELS.NAME',
	CITY = 'SITES.LABELS.CITY',
	DESCRIPTOR = 'SITES.LABELS.DESCRIPTOR',
	CONTACT_NAME = 'SITES.LABELS.CONTACT_NAME',
	URL = 'SITES.LABELS.URL',
	PHONE = 'SITES.LABELS.PHONE',
	EMAIL = 'SITES.LABELS.EMAIL'
}

export const getSitesDetailsSchema = (t: TFunction): Yup.SchemaOf<SiteTypes> => Yup.object<Shape<SiteTypes>>().shape({
	[FORM_FIELDS.NAME]: Yup.string()
		.required(t('SITES.VALIDATION.NAME_REQUIRED')),
	[FORM_FIELDS.DESCRIPTOR]: Yup.string()
		// .max(12, t('SITES.VALIDATION.DESCRIPTOR_REQUIRED_LENGTH'))
		.required(t('SITES.VALIDATION.DESCRIPTOR_REQUIRED')),
	[FORM_FIELDS.CITY]: Yup.string()
		.required(t('SITES.VALIDATION.CITY_REQUIRED')),
	[FORM_FIELDS.CONTACT_NAME]: Yup.string()
		.required(t('SITES.VALIDATION.CONTACT_NAME_REQUIRED')),
	[FORM_FIELDS.URL]: Yup.string()
		.url(t('NEW_APPLICATION.VALIDATION.WEB_URL_FORMAT'))
		.required(t('SITES.VALIDATION.URL_REQUIRED')),
	[FORM_FIELDS.PHONE]: Yup.number()
		.test('is-valid-phone', t('NEW_APPLICATION.VALIDATION.VALID_PHONE'), (value) => {
      if (!value) return true;
      const phoneRegex = /^[0-9]{10,15}$/;
      return phoneRegex.test(value.toString());
    })
		.typeError(t('NEW_APPLICATION.VALIDATION.NUMBER_ERROR_TEXT'))
		.required(t('SITES.VALIDATION.PHONE_REQUIRED')),
	[FORM_FIELDS.EMAIL]: Yup.string()
		.email(t('NEW_APPLICATION.VALIDATION.EMAIL_ERROR_TEXT'))
		.required(t('SITES.VALIDATION.EMAIL_REQUIRED')),
	[FORM_FIELDS.WHITELISTED_URLS]: Yup.array()
		.test('no site', t('SITES.VALIDATION.WHITELISTED_URLS_REQUIRED'), (item) => {
			return Boolean(item.length);
		})
		.test('valid url', t('SITES.VALIDATION.WHITELISTED_URL_FORMAT'), (item) => {
			const res = item.some(el => !validateUrl(el));
			return !res;
		})
		.typeError(t('SITES.VALIDATION.WHITELISTED_URLS_REQUIRED'))
		.required(t('SITES.VALIDATION.WHITELISTED_URLS_REQUIRED')),
	[FORM_FIELDS.TAGS]: Yup.array(),
});

export const initialValues: SiteTypes = {
	[FORM_FIELDS.NAME]: '',
	[FORM_FIELDS.DESCRIPTOR]: '',
	[FORM_FIELDS.CITY]: '',
	[FORM_FIELDS.CONTACT_NAME]: '',
	[FORM_FIELDS.URL]: '',
	[FORM_FIELDS.PHONE]: '',
	[FORM_FIELDS.EMAIL]: '',
	[FORM_FIELDS.WHITELISTED_URLS]: [],
	[FORM_FIELDS.TAGS]: [],
};

export const EditSiteValues = (siteData): SiteTypes => ({
	[FORM_FIELDS.NAME]: siteData?.name || '',
	[FORM_FIELDS.DESCRIPTOR]: siteData?.descriptor || '',
	[FORM_FIELDS.CITY]: siteData?.descriptorCity || '',
	[FORM_FIELDS.CONTACT_NAME]: siteData?.customerSupportName || '',
	[FORM_FIELDS.URL]: siteData?.customerSupportLink || '',
	[FORM_FIELDS.PHONE]: siteData?.customerSupportPhone || '',
	[FORM_FIELDS.EMAIL]: siteData?.customerSupportEmail || '',
	[FORM_FIELDS.WHITELISTED_URLS]: siteData?.whitelistedUrls ?? [],
	[FORM_FIELDS.TAGS]: siteData?.tags ?? [],
});

export const convertToInitial = (data): SiteTypes => ({
	[FORM_FIELDS.NAME]: data?.name || [],
	[FORM_FIELDS.DESCRIPTOR]: data?.descriptor || [],
	[FORM_FIELDS.CITY]: data?.descriptorCity || [],
	[FORM_FIELDS.CONTACT_NAME]: data?.customerSupportName || [],
	[FORM_FIELDS.URL]: data?.customerSupportLink || [],
	[FORM_FIELDS.PHONE]: data?.customerSupportPhone || [],
	[FORM_FIELDS.EMAIL]: data?.customerSupportEmail || [],
	[FORM_FIELDS.WHITELISTED_URLS]: data?.whitelistedUrls || [],
	[FORM_FIELDS.TAGS]: data?.tags || [],
});
